import React, { useEffect, useState, useMemo } from 'react'
import PageHeader from '../../shared/PageHeader'
import {
  MdEditSquare,
  MdNoteAdd,
  MdOutlineHistory,
  MdVerified
} from 'react-icons/md'
import moment from 'moment'
import ApprovalManagementFilter from '../../components/his/rcm/ApprovalManagementFilter'
import CustomTable from '../../shared/CustomTable'
import { useTranslation } from 'react-i18next'
import CustomPagination from '../../shared/CustomPagination'
import ActionButton from '../../components/his/visits/ActionButton'
import {
  setIsGeneralSearch,
  setSelectPatientData
} from '../../redux/slices/BeneficiarySlice'
import { setSelectedVisitData } from '../../redux/slices/VisitsSlice'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import DropdownField from '../../shared/DropdownField'
import { FaFileAlt, FaFileInvoiceDollar, FaListUl } from 'react-icons/fa'
import { fetchData } from '../../helpers/fetchData'
import OcafForm from '../../templates/OcafForm'
import DcafForm from '../../templates/DcafForm'
import UcafForm from '../../templates/UcafForm'
import CheckPermission from '../../hooks/CheckPermission'
import CustomTabs from '../../shared/CustomTabs'
import CustomChip from '../../shared/CustomChip'
import { EditIcon } from '../../utils/TableActionIcons'
import { navigateSecurely } from '../../utils/secureNavigation'
import usePersistedFilters from '../../hooks/usePersistedFilters'
import ReusableModal from '../../shared/ReuseableModal'
import EditEmrItemsModal from '../../components/his/visits/EditEmrItemsModal'
import ManualApprovalItemsModal from '../../components/his/visits/ManualApprovalItemsModal'
import { useGetInsuranceVisitsWithoutSlotsQuery } from '../../redux/api/modules/claimsApi'

export const CafFormComponent = ({
  selectedData,
  setShowForm,
  showForm,
  setSelectedData
}) => {
  if (!selectedData) return null

  const dept = selectedData?.paractitioner?.department?.name_en?.toLowerCase()
  if (dept === 'ophthalmology') {
    return (
      <OcafForm
        item={selectedData}
        setShowOcaf={setShowForm}
        showOcaf={showForm}
        setSelectedData={setSelectedData}
        edit={true}
      />
    )
  } else if (
    dept === 'dentistry' ||
    dept === 'dental' ||
    dept === 'oral and dental'
  ) {
    return (
      <DcafForm
        item={selectedData}
        setShowDCAF={setShowForm}
        showDCAF={showForm}
        setSelectedData={setSelectedData}
        edit={true}
      />
    )
  } else {
    return (
      <UcafForm
        item={selectedData}
        setShowUCAF={setShowForm}
        showUCAF={showForm}
        setSelectedData={setSelectedData}
        edit={true}
      />
    )
  }
}

function ApprovalManagement () {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { hasPermission } = CheckPermission()
  const [selectedEmrData, setSelectedEmrData] = useState(null)
  const [selectedEditEmrItemsData, setSelectedEditEmrItemsData] = useState(null)
  const [selectedEditItemsVisitData, setSelectedEditItemsVisitData] =
    useState(null)
  const [openEditEmrItemsModal, setOpenEditEmrItemsModal] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [loadingEmrId, setLoadingEmrId] = useState(null)
  const [loadingEditItemsEmrId, setLoadingEditItemsEmrId] = useState(null)
  const [loadingManualApprovalEmrId, setLoadingManualApprovalEmrId] =
    useState(null)
  const [selectedManualApprovalEmrData, setSelectedManualApprovalEmrData] =
    useState(null)
  const [selectedManualApprovalVisitData, setSelectedManualApprovalVisitData] =
    useState(null)
  const [openManualApprovalModal, setOpenManualApprovalModal] = useState(false)
  const auth = useSelector(state => state.auth)
  const userRoleId = Number(
    auth?.user?.role_id ||
      auth?.user?.role?.id ||
      auth?.user?.role?._id ||
      auth?.roleData?.role_id ||
      0
  )
  const hasManualApprovalType =
    auth?.insuranceApprovalSettings &&
    Object.values(auth.insuranceApprovalSettings).some(value => value === false)
  const canManuallySendApproval = userRoleId === 11 && hasManualApprovalType
  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    const saved = sessionStorage.getItem('approvalManagement_activeTab')
    return saved ? parseInt(saved) : 0
  })

  const defaultFilters = {
    page: 1,
    perPage: 20,
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    practitioner_id: '',
    department_id: '',
    patient_file_number: '',
    patient_name: '',
    patient_document_id: '',
    payer_id: '',
    policy_number: '',
    batch_status: ''
  }

  const {
    filters: payload,
    setFilters,
    appliedFilters,
    applyFilters,
    onPageChange: onPersistedPageChange
  } = usePersistedFilters('approvalManagement', defaultFilters, {
    persistPage: true
  })

  useEffect(() => {
    sessionStorage.setItem(
      'approvalManagement_activeTab',
      activeTabIndex.toString()
    )
    const nextFilters = {
      ...payload,
      batch_status: activeTabIndex === 1 ? 'pending' : (payload?.batch_status || ''),
      page: 1
    }
    applyFilters(nextFilters)
  }, [activeTabIndex])

  const queryPayload = useMemo(() => {
    const filters = { ...appliedFilters }
    delete filters.per_page
    delete filters.page_size

    return {
      ...filters,
      page: appliedFilters?.page || 1,
      perPage: appliedFilters?.perPage || appliedFilters?.per_page || appliedFilters?.page_size || 20,
      batch_status: activeTabIndex === 1 ? 'pending' : (appliedFilters?.batch_status || ''),
    }
  }, [appliedFilters, activeTabIndex])

  const {
    data,
    isLoading: queryLoading,
    isFetching: queryFetching,
    refetch
  } = useGetInsuranceVisitsWithoutSlotsQuery(queryPayload)
  const loading = queryLoading || queryFetching

  const handleSetPayload = updater => {
    const newPayload = typeof updater === 'function' ? updater(payload) : updater
    setFilters(newPayload)
  }

  const handleSearch = (searchPayload = payload) => {
    const newPayload = {
      ...searchPayload,
      batch_status:
        activeTabIndex === 1 ? 'pending' : searchPayload?.batch_status,
      page: 1,
      perPage: searchPayload?.perPage || 20
    }
    applyFilters(newPayload)
  }

  const handleEditEmrItems = async (emrNumber, rowData) => {
    setLoadingEditItemsEmrId(emrNumber)
    const payload = {
      emr_id: Number(emrNumber),
      visit_id: rowData?.visit_id,
      patient_id: rowData?.patient?.patient_id
    }
    try {
      const response = await fetchData('POST', '/v3/emr/getById', payload)
      if (response?.data) {
        dispatch(setSelectPatientData(rowData?.patient))
        dispatch(setSelectedVisitData(rowData))
        setSelectedEditItemsVisitData(rowData)
        setSelectedEditEmrItemsData({
          ...response.data,
          ...payload,
          items: response?.data?.items || []
        })
        setOpenEditEmrItemsModal(true)
      } else {
        setSelectedEditEmrItemsData(null)
        setSelectedEditItemsVisitData(null)
      }
    } finally {
      setLoadingEditItemsEmrId(null)
    }
  }

  const onPageChange = (newPage, newRowsPerPage) => {
    onPersistedPageChange(newPage, newRowsPerPage || queryPayload.perPage)
  }

  const handleOpenManualApproval = async (emrNumber, rowData) => {
    setLoadingManualApprovalEmrId(emrNumber)
    const payload = {
      emr_id: Number(emrNumber),
      visit_id: rowData?.visit_id,
      patient_id: rowData?.patient?.patient_id
    }
    try {
      const response = await fetchData('POST', '/v3/emr/getById', payload)
      if (response?.data) {
        setSelectedManualApprovalVisitData(rowData)
        setSelectedManualApprovalEmrData({
          ...response.data,
          ...payload,
          items: response?.data?.items || []
        })
        setOpenManualApprovalModal(true)
      } else {
        setSelectedManualApprovalVisitData(null)
        setSelectedManualApprovalEmrData(null)
      }
    } finally {
      setLoadingManualApprovalEmrId(null)
    }
  }

  const columns = [
    // {
    //   field: 'visit_date',
    //   header: t('VisitDate'),
    //   body: rowData => formatDateTime(rowData?.visit_date)
    // },
    // {
    //   field: 'visit_id',
    //   header: t('VisitNo')
    // },
    {
      field: 'file_no',
      header: t('MRN'),
      body: rowData => rowData?.patient?.file_no
    },
    {
      field: 'document_id',
      header: t('DocumentID'),
      body: rowData => rowData?.patient?.document_id
    },
    {
      field: 'patient_name',
      header: t('PatientName'),
      body: rowData => rowData?.patient?.full_name
    },
    {
      field: 'practitioner_name',
      header: t('Practitioner'),
      body: rowData => rowData?.practitioner?.name
    },
    {
      field: 'department',
      header: t('Department'),
      body: rowData =>
        rowData?.department?.name_en ||
        rowData?.practitioner?.department?.name_en
    },
    {
      field: 'status',
      header: t('Status'),
      body: rowData => {
        const status = rowData?.batch_status || rowData?.status
        const getChipType = status => {
          const statusLower = status?.toLowerCase()
          if (statusLower === 'approved') return 'success'
          if (statusLower === 'pending') return 'default'
          if (statusLower === 'rejected') return 'danger'
          if (statusLower === 'partial approved') return 'warning'
          return 'soft-secondary'
        }

        return (
          <CustomChip
            chipType={getChipType(status)}
            label={status}
            className='!h-[20px] capitalize'
            labelClassName='!text-[11px]'
          />
        )
      }
    },
    {
      field: 'payer_name',
      header: t('PayerName'),
      body: rowData => rowData?.patient?.selected_plan?.payer_name || '-'
    },
    {
      field: 'policy_number',
      header: t('Policy'),
      body: rowData => rowData?.patient?.selected_plan?.policy_number || '-'
    }
  ]
  const action = {
    body: rowData => (
      <div className='flex items-center gap-1'>
        {!rowData?.emr_numbers?.length &&
          rowData?.status !== 'Cancelled' &&
          hasPermission('emr', 'create') && (
            <ActionButton
              icon={<MdNoteAdd size={18} />}
              variant='primary'
              sizeChange={false}
              onClick={() => {
                dispatch(setSelectPatientData(rowData?.patient))
                dispatch(setIsGeneralSearch(false))
                dispatch(setSelectedVisitData(rowData))
                navigateSecurely(navigate, '/patient-profile/create-emr', {
                  rowData
                })
              }}
              className='!w-7 !h-7 !p-0 !border-none !bg-transparent hover:!bg-transparent shadow-none !text-primary'
              title={t('CreateEMR')}
            />
          )}
        {rowData?.emr_numbers?.length > 0 &&
          rowData?.emr_numbers?.map((d, i) => {
            return (
              <div key={i} className='flex items-center gap-1'>
                <EditIcon
                  iconSize='15'
                  title={t('EditEMR')}
                  onClick={() => {
                    dispatch(setSelectPatientData(rowData?.patient))
                    dispatch(setSelectedVisitData(rowData))
                    navigateSecurely(navigate, '/patient-profile/edit-emr', {
                      emr_id: d,
                      visitData: rowData
                    })
                  }}
                />
                {(hasPermission('approval_management', 'update') || hasPermission('emr', 'update')) && <ActionButton
                  icon={<FaListUl size={15} />}
                  variant='warning'
                  sizeChange={false}
                  loading={loadingEditItemsEmrId === d}
                  onClick={() => handleEditEmrItems(d, rowData)}
                  className='!w-7 !h-7 !p-0 !border-none !bg-transparent hover:!bg-transparent shadow-none !text-amber-600'
                  title={t('Edit EMR Items')}
                />}
                {canManuallySendApproval && (hasPermission('approval_management', 'update') || hasPermission('emr', 'update')) && (
                  <ActionButton
                    icon={<MdVerified size={18} />}
                    variant='primary'
                    sizeChange={false}
                    loading={loadingManualApprovalEmrId === d}
                    onClick={() => handleOpenManualApproval(d, rowData)}
                    className='!w-7 !h-7 !p-0 !border-none !bg-transparent hover:!bg-transparent shadow-none !text-blue'
                    title={t('Send Approval Request')}
                  />
                )}
                <ActionButton
                  icon={<MdOutlineHistory size={18} />}
                  variant='secondary'
                  sizeChange={false}
                  onClick={() => {
                    navigate('/emr-history', { state: { emr_id: d } })
                  }}
                  className='!w-7 !h-7 !p-0 !border-none !bg-transparent hover:!bg-transparent shadow-none !text-gray-500'
                  title={t('EMRHistory')}
                />
                <ActionButton
                  icon={<FaFileAlt size={16} />}
                  variant='secondary'
                  sizeChange={false}
                  loading={loadingEmrId === d}
                  onClick={async () => {
                    try {
                      setLoadingEmrId(d)
                      let payload = {
                        emr_id: Number(d),
                        patient_id: rowData?.patient?.patient_id,
                        visit_id: rowData?.visit_id
                      }
                      const response = await fetchData(
                        'POST',
                        '/v3/emr/getById',
                        payload
                      )
                      if (response?.data) {
                        setSelectedEmrData(response?.data)
                        setShowForm(true)
                      }
                    } catch (error) {
                      console.log(error)
                    } finally {
                      setLoadingEmrId(null)
                    }
                  }}
                  className='!w-7 !h-7 !p-0 !border-none !bg-transparent hover:!bg-transparent shadow-none !text-gray-500'
                  title={
                    rowData?.department?.name_en?.toLowerCase() ==
                    'ophthalmology'
                      ? t('Print OCAF')
                      : rowData?.department?.name_en?.toLowerCase() ==
                          'dentistry' ||
                        rowData?.department?.name_en?.toLowerCase() ==
                          'dental' ||
                        rowData?.department?.name_en?.toLowerCase() ==
                          'oral and dental'
                      ? t('Print DCAF')
                      : t('Print UCAF')
                  }
                />
              </div>
            )
          })}

        {Array.isArray(rowData?.reference_invoice_number) &&
          rowData.reference_invoice_number.length > 0 && (
            <div className='relative group'>
              <DropdownField
                useFormik={false}
                placeholder={
                  <div className='flex items-center gap-1'>
                    <FaFileInvoiceDollar
                      size={14}
                      className='text-primary/60 group-hover:text-primary transition-colors'
                    />
                    <span className='text-[10px] font-semibold text-primary/60 uppercase md:block hidden'>
                      {t('Invoices')}
                    </span>
                  </div>
                }
                className='!w-auto !h-7 !mb-0 visit-card-dropdownfield-simple'
                options={rowData?.reference_invoice_number?.map(v => ({
                  label: v?.insurance_invoice_number,
                  value: v?.invoice_number
                }))}
                handleChange={e => {
                  navigate(`/print-invoice/${e.value}`, {
                    state: { invoiceNumber: e.value, isRcm: true }
                  })
                }}
                ptOptions={{
                  root: {
                    className:
                      '!h-7 !min-h-[28px] flex items-center !rounded-[6px] !bg-white !border !border-gray-200 hover:!border-gray-300 transition-all !px-2 shadow-none'
                  },
                  input: { className: 'hidden' },
                  trigger: {
                    className: '!w-full flex items-center justify-center gap-2'
                  },
                  dropdownicon: {
                    className:
                      '!w-[8px] md:!w-[10px] !h-[8px] md:!h-[10px] !text-gray-400 group-hover:!text-gray-600'
                  },
                  item: {
                    className:
                      'capitalize !outline-none text-[10px] md:text-[11px]'
                  }
                }}
              />
            </div>
          )}
      </div>
    ),
    header: t('Actions')
  }
  const allColumns = [action, ...columns]

  const batchStatusOptions = [
    { label: t('Approved'), value: 'approved' },
    { label: t('Pending'), value: 'pending' },
    { label: t('Rejected'), value: 'rejected' },
    { label: t('Partial Approved'), value: 'partial approved' }
  ]

  const renderApprovalManagementContent = () => (
    <>
      <ApprovalManagementFilter
        payload={payload}
        setPayload={handleSetPayload}
        onSearch={handleSearch}
        loading={loading}
        showBatchStatus={true}
        batchStatusOptions={batchStatusOptions}
      />
      <div className='mt-2'>
        <CustomTable
          columns={allColumns}
          data={data?.data}
          isLoading={loading}
        />
        <CustomPagination
          currentPage={queryPayload.page}
          rowsPerPage={queryPayload.perPage}
          totalRecords={data?.totalCount || 0}
          totalPages={
            data?.totalCount && queryPayload.perPage
              ? Math.ceil(data?.totalCount / queryPayload.perPage)
              : 1
          }
          onPageChange={onPageChange}
        />
      </div>
    </>
  )

  const renderPendingApprovalsContent = () => (
    <>
      <ApprovalManagementFilter
        payload={payload}
        setPayload={handleSetPayload}
        onSearch={handleSearch}
        loading={loading}
        showBatchStatus={false}
      />
      <div className='mt-2'>
        <CustomTable
          columns={allColumns}
          data={data?.data}
          isLoading={loading}
        />
        <CustomPagination
          currentPage={queryPayload.page}
          rowsPerPage={queryPayload.perPage}
          totalRecords={data?.totalCount || 0}
          totalPages={
            data?.totalCount && queryPayload.perPage
              ? Math.ceil(data?.totalCount / queryPayload.perPage)
              : 1
          }
          onPageChange={onPageChange}
        />
      </div>
    </>
  )

  const tabs = [
    {
      name: t('Approval Management'),
      content: renderApprovalManagementContent
    },
    {
      name: t('Pending Approvals'),
      content: renderPendingApprovalsContent
    }
  ]

  return (
    <div className='main-page-container !overflow-y-auto scrollbar w-full p-4'>
      {/* <PageHeader title={t('Approval Management')} Icon={MdVerified} /> */}
      <CustomTabs
        tabs={tabs}
        activeTabIndex={activeTabIndex}
        setActiveTabIndex={setActiveTabIndex}
      />
      <CafFormComponent
        selectedData={{
          ...selectedEmrData,
          patient: selectedEmrData?.patient,
          paractitioner: selectedEmrData?.practitioner
        }}
        setSelectedData={() => {}}
        setShowForm={setShowForm}
        showForm={showForm}
        dept={selectedEmrData?.department?.name_en?.toLowerCase()}
      />
      {openEditEmrItemsModal &&
        selectedEditEmrItemsData &&
        selectedEditItemsVisitData && (
          <ReusableModal
            position='top'
            isVisible={openEditEmrItemsModal}
            title={t('Edit EMR Items')}
            HeaderIcon={MdEditSquare}
            onClose={() => {
              setOpenEditEmrItemsModal(false)
              setSelectedEditEmrItemsData(null)
              setSelectedEditItemsVisitData(null)
            }}
            className='w-full lg:w-[95%] relative'
          >
            <EditEmrItemsModal
              emrData={selectedEditEmrItemsData}
              visitData={selectedEditItemsVisitData}
              selectedPatientData={selectedEditItemsVisitData?.patient}
              onClose={() => {
                setOpenEditEmrItemsModal(false)
                setSelectedEditEmrItemsData(null)
                setSelectedEditItemsVisitData(null)
              }}
              onUpdated={async items => {
                setSelectedEditEmrItemsData(prev =>
                  prev ? { ...prev, items } : prev
                )
                if (refetch) refetch()
              }}
            />
          </ReusableModal>
        )}
      {openManualApprovalModal &&
        selectedManualApprovalEmrData &&
        selectedManualApprovalVisitData && (
          <ReusableModal
            position='top'
            isVisible={openManualApprovalModal}
            title={t('Send Approval Request')}
            HeaderIcon={MdVerified}
            onClose={() => {
              setOpenManualApprovalModal(false)
              setSelectedManualApprovalEmrData(null)
              setSelectedManualApprovalVisitData(null)
            }}
            className='w-full lg:w-[95%] xl:w-[1200px] relative'
          >
            <ManualApprovalItemsModal
              emrData={selectedManualApprovalEmrData}
              visitData={selectedManualApprovalVisitData}
              selectedPatientData={selectedManualApprovalVisitData?.patient}
              onClose={() => {
                setOpenManualApprovalModal(false)
                setSelectedManualApprovalEmrData(null)
                setSelectedManualApprovalVisitData(null)
              }}
              onUpdated={async items => {
                setSelectedManualApprovalEmrData(prev =>
                  prev ? { ...prev, items } : prev
                )
                if (refetch) refetch()
              }}
            />
          </ReusableModal>
        )}
    </div>
  )
}

export default ApprovalManagement
